"use client";

import Image from "next/image";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ChartColumn,
  ChevronDown,
  Globe,
  ImageIcon,
  LoaderCircle,
  MessageCircleQuestionMark,
  Paperclip,
  Plus,
  Trash2,
  UsersRound,
} from "lucide-react";

import { ImageRedactionEditor } from "@/components/feed/image-redaction-editor";
import { LinkPreviewCard } from "@/components/feed/link-preview-card";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/ui/file-dropzone";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useCurrentUser, useMyCommunities } from "@/hooks/use-current-user";
import { useCreatePost } from "@/hooks/use-feed";
import { useLinkPreview } from "@/hooks/use-link-preview";
import {
  ALLOWED_DOCUMENT_TYPES,
  ALLOWED_MEDIA_TYPES,
  MAX_ATTACHMENT_BYTES,
  acceptAttribute,
  attachmentTypeFromMime,
  isAllowedDocumentType,
  isAllowedMediaType,
  postTypeFromAttachments,
  resolvedMimeType,
} from "@/lib/feed/types";
import { cn, createId, getErrorMessage } from "@/lib/utils";

const MAX_FILES = 4;
const DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  ".odt",
  ".ods",
  ".odp",
  ".txt",
  ".rtf",
];
const MEDIA_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".mp4",
  ".webm",
  ".mov",
];

type DraftFile = {
  id: string;
  file: File;
};

type AttachmentPicker = "document" | "media" | null;
type ComposerIntent = "post" | "question" | "poll";

type DraftPollOption = {
  id: string;
  label: string;
};

function initialPollOptions(): DraftPollOption[] {
  return [
    { id: createId(), label: "" },
    { id: createId(), label: "" },
  ];
}

type CreatePostFormProps = {
  hero?: boolean;
  lockedCommunity?: {
    id: string;
    name: string;
  };
};

export function CreatePostForm({
  hero = false,
  lockedCommunity,
}: CreatePostFormProps) {
  const currentUserQuery = useCurrentUser();
  const communitiesQuery = useMyCommunities(
    currentUserQuery.data?.accountStatus === "active" && !lockedCommunity,
  );
  const createPost = useCreatePost();
  const [content, setContent] = useState("");
  const [audience, setAudience] = useState<"global" | "community">("global");
  const [communityId, setCommunityId] = useState("");
  const [files, setFiles] = useState<DraftFile[]>([]);
  const [redactMediaIndex, setRedactMediaIndex] = useState<number | null>(null);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [picker, setPicker] = useState<AttachmentPicker>(null);
  const [intent, setIntent] = useState<ComposerIntent>("post");
  const [pollOptions, setPollOptions] = useState<DraftPollOption[]>(
    initialPollOptions,
  );
  const [pollAllowOther, setPollAllowOther] = useState(true);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!hero);
  const [formError, setFormError] = useState<string | null>(null);
  const linkPreview = useLinkPreview(content, intent !== "poll");

  useEffect(() => {
    if (linkPreview.loading || linkPreview.preview) {
      setIsExpanded(true);
    }
  }, [linkPreview.loading, linkPreview.preview]);

  const user = currentUserQuery.data;
  const communities = communitiesQuery.data ?? [];
  const effectiveAudience = lockedCommunity ? "community" : audience;
  const effectiveCommunityId = lockedCommunity?.id ?? communityId;
  const remaining = 2000 - content.length;
  const documentFiles = files.filter((item) => isAllowedDocumentType(item.file));
  const mediaFiles = files.filter((item) => isAllowedMediaType(item.file));
  const redactDraft =
    redactMediaIndex == null ? null : (mediaFiles[redactMediaIndex] ?? null);
  const documentAccept = useMemo(
    () => acceptAttribute(ALLOWED_DOCUMENT_TYPES, DOCUMENT_EXTENSIONS),
    [],
  );
  const mediaAccept = useMemo(
    () => acceptAttribute(ALLOWED_MEDIA_TYPES, MEDIA_EXTENSIONS),
    [],
  );

  function resetForm() {
    setContent("");
    setAudience("global");
    setCommunityId("");
    setFiles([]);
    setRedactMediaIndex(null);
    setAudienceOpen(false);
    setPicker(null);
    setIntent("post");
    setPollOptions(initialPollOptions());
    setPollAllowOther(true);
    setPollAllowMultiple(false);
    setIsExpanded(!hero);
    setFormError(null);
    linkPreview.reset();
  }

  function replaceCategory(
    kind: "document" | "media",
    selectedFiles: File[],
  ) {
    const kept = files.filter((item) =>
      kind === "document"
        ? isAllowedMediaType(item.file)
        : isAllowedDocumentType(item.file),
    );
    const nextFiles: DraftFile[] = [];
    const errors: string[] = [];

    selectedFiles.forEach((file) => {
      if (kept.length + nextFiles.length >= MAX_FILES) {
        errors.push(`Legfeljebb ${MAX_FILES} fájl csatolható.`);
        return;
      }

      const allowed =
        kind === "document"
          ? isAllowedDocumentType(file)
          : isAllowedMediaType(file);

      if (!allowed) {
        errors.push(`Nem támogatott fájltípus: ${file.name}`);
        return;
      }

      if (file.size > MAX_ATTACHMENT_BYTES) {
        errors.push(`${file.name} nagyobb, mint 15 MB.`);
        return;
      }

      const existing = files.find(
        (item) =>
          item.file.name === file.name &&
          item.file.lastModified === file.lastModified,
      );
      nextFiles.push(existing ?? { id: createId(), file });
    });

    setFiles([...kept, ...nextFiles]);
    setFormError(errors[0] ?? null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || user.accountStatus !== "active") {
      setFormError(
        user
          ? "Bejegyzés írásához jóváhagyott, aktív fiók szükséges."
          : "Bejegyzés írásához be kell jelentkezned.",
      );
      return;
    }

    const trimmed = content.trim();
    const normalizedPollOptions = pollOptions
      .map((option) => ({ ...option, label: option.label.trim() }))
      .filter((option) => option.label);

    if (intent === "poll" && !trimmed) {
      setIsExpanded(true);
      setFormError("Adja meg a szavazás kérdését.");
      return;
    }

    if (intent !== "poll" && !trimmed && files.length === 0) {
      setIsExpanded(true);
      setFormError("Írj szöveget, vagy csatolj egy fájlt.");
      return;
    }

    if (intent === "poll") {
      if (normalizedPollOptions.length < 2) {
        setFormError("Adjon meg legalább két válaszlehetőséget.");
        return;
      }
      if (
        new Set(
          normalizedPollOptions.map((option) => option.label.toLocaleLowerCase("hu-HU")),
        ).size !== normalizedPollOptions.length
      ) {
        setFormError("A válaszlehetőségek nem lehetnek azonosak.");
        return;
      }
    }

    if (effectiveAudience === "community" && !effectiveCommunityId) {
      setFormError("Válassz közösséget a bejegyzéshez.");
      return;
    }

    setFormError(null);

    try {
      await createPost.mutateAsync({
        content: trimmed,
        audience: effectiveAudience,
        communityId:
          effectiveAudience === "community" ? effectiveCommunityId : null,
        postType:
          intent === "poll"
            ? "poll"
            : intent === "question"
            ? "question"
            : postTypeFromAttachments(
                files.map((item) => ({
                  type: attachmentTypeFromMime(resolvedMimeType(item.file)),
                })),
              ),
        poll:
          intent === "poll"
            ? {
                options: normalizedPollOptions,
                allowOther: pollAllowOther,
                allowMultiple: pollAllowMultiple,
              }
            : undefined,
        files:
          intent === "poll" ? [] : files.map((item) => item.file),
        userId: user.id,
        linkPreview: intent === "poll" ? null : linkPreview.preview,
      });
      resetForm();
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  }

  if (!user || user.accountStatus !== "active") {
    return (
      <section className="shadow-surface rounded-2xl border bg-card p-4 text-sm text-muted-foreground">
        {user
          ? "Bejegyzés írásához jóváhagyott, aktív PraxisTárs fiók szükséges."
          : "Bejegyzés írásához jelentkezz be egy PraxisTárs fiókkal."}
      </section>
    );
  }

  return (
    <section
      id={hero ? "new-post" : undefined}
      className={cn(
        "shadow-surface relative scroll-mt-20 overflow-hidden rounded-none border border-x-0 border-black/[0.06] bg-card p-5 md:rounded-2xl md:border-x",
        hero && "max-md:px-3 max-md:py-1.5",
        hero &&
          !isExpanded &&
          "max-md:mx-4 max-md:mt-[5px] max-md:mb-5 max-md:rounded-[20px] max-md:border-x",
      )}
    >
      {!lockedCommunity ? (
        <div className={cn(hero && "max-md:hidden")}>
          <ComposerIllustration home={hero} />
        </div>
      ) : null}

      <form
        className={cn(
          "relative space-y-4",
          hero && !isExpanded && "max-md:space-y-0",
        )}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            "flex min-h-12 items-center gap-3",
            isExpanded && "max-md:hidden",
            hero && !isExpanded && "max-md:min-h-0 max-md:gap-2",
          )}
        >
          <UserAvatar
            name={user.fullName}
            path={user.avatarPath}
            className={cn("size-11 text-sm", hero && "max-md:size-8")}
          />
          <button
            type="button"
            className={cn(
              "min-w-0 flex-1 pr-16 text-left sm:pr-52",
              hero &&
                !isExpanded &&
                "max-md:rounded-full max-md:border max-md:border-slate-200 max-md:bg-background max-md:px-3 max-md:py-1.5",
            )}
            onClick={() => setIsExpanded(true)}
          >
            <span
              className={cn(
                "block text-base font-semibold tracking-tight text-slate-900",
                hero && !isExpanded && "max-md:hidden",
              )}
            >
              {hero
                ? "Ossza meg kollégáival"
                : `Mi jár a fejében, ${user.fullName}?`}
            </span>
            {hero && !isExpanded ? (
              <span className="hidden text-sm text-muted-foreground max-md:block">
                Mit osztana meg?
              </span>
            ) : null}
            {hero ? (
              <span
                className={cn(
                  "mt-1 block text-sm leading-5 text-muted-foreground",
                  !isExpanded && "max-md:hidden",
                )}
              >
                Szakmai tapasztalat, kérdés vagy hasznos információ egy helyen.
              </span>
            ) : null}
          </button>
          {hero && !isExpanded ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-emerald-600 md:hidden"
              aria-label="Kép vagy videó megosztása"
              onClick={() => {
                setIsExpanded(true);
                setIntent("post");
                setPicker("media");
              }}
            >
              <ImageIcon className="size-5" />
            </Button>
          ) : null}
        </div>

        <div
          className={cn(
            "relative transition-all",
            isExpanded
              ? "rounded-xl border border-slate-200/90 bg-white/90 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)] backdrop-blur-[2px]"
              : "px-4 py-2 sm:max-w-[62%]",
            hero && !isExpanded && "max-md:hidden",
          )}
        >
          {!content && intent === "post" ? (
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute text-sm text-muted-foreground",
                isExpanded ? "top-5 left-6" : "top-2 left-4",
              )}
            >
              Kezdje el írni a bejegyzést
              <span className="typing-ellipsis">...</span>
            </span>
          ) : null}
          <Textarea
            aria-label="Bejegyzés szövege"
            value={content}
            onFocus={() => setIsExpanded(true)}
            onChange={(event) => setContent(event.target.value.slice(0, 2000))}
            placeholder={
              intent === "question"
                ? "Tegyen fel egy szakmai kérdést a közösségnek..."
                : intent === "poll"
                  ? "Mi legyen a szavazás kérdése?"
                : undefined
            }
            rows={isExpanded ? 3 : 1}
            className={cn(
              "resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0",
              isExpanded ? "min-h-[5.5rem] px-2 py-1" : "min-h-6",
            )}
          />
          {isExpanded ? (
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-200/70 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 rounded-full px-2 text-xs text-muted-foreground"
                onClick={() => !lockedCommunity && setAudienceOpen((open) => !open)}
              >
                {lockedCommunity || audience === "community" ? (
                  <UsersRound className="size-3.5" />
                ) : (
                  <Globe className="size-3.5" />
                )}
                {lockedCommunity?.name ??
                  (audience === "community"
                    ? communities.find((community) => community.id === communityId)
                        ?.name || "Közösség kiválasztása"
                    : "Minden PraxisTárs-tag")}
                {!lockedCommunity ? (
                  <ChevronDown
                    className={cn(
                      "size-3.5 transition-transform",
                      audienceOpen && "rotate-180",
                    )}
                  />
                ) : null}
              </Button>
              <p className="text-xs text-muted-foreground">{remaining} karakter</p>
            </div>
          ) : null}
        </div>

        {isExpanded && audienceOpen && !lockedCommunity ? (
          <div className="rounded-xl border bg-muted/20 p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Kik láthatják a bejegyzést?
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={audience === "global" ? "secondary" : "ghost"}
                onClick={() => {
                  setAudience("global");
                  setCommunityId("");
                }}
              >
                <Globe className="size-4" />
                Minden tag
              </Button>
              <Button
                type="button"
                size="sm"
                variant={audience === "community" ? "secondary" : "ghost"}
                onClick={() => setAudience("community")}
              >
                <UsersRound className="size-4" />
                Egy közösség
              </Button>
            </div>
            {audience === "community" ? (
              <select
                value={communityId}
                onChange={(event) => setCommunityId(event.target.value)}
                className="mt-3 h-10 w-full rounded-xl border bg-card px-3 text-sm outline-none focus:border-primary"
              >
                <option value="">Válasszon közösséget</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ) : null}

        {isExpanded && intent === "poll" ? (
          <PollEditor
            options={pollOptions}
            allowOther={pollAllowOther}
            allowMultiple={pollAllowMultiple}
            onOptionsChange={setPollOptions}
            onAllowOtherChange={setPollAllowOther}
            onAllowMultipleChange={setPollAllowMultiple}
          />
        ) : null}

        {picker === "document" || documentFiles.length > 0 ? (
          <FileDropzone
            multiple
            compact
            privacyNotice
            accept={documentAccept}
            files={documentFiles.map((item) => item.file)}
            onFilesChange={(nextFiles) => replaceCategory("document", nextFiles)}
            title="Dokumentum csatolása"
            description="PDF, Word, PowerPoint, Excel, legfeljebb 15 MB"
          />
        ) : null}

        {picker === "media" || mediaFiles.length > 0 ? (
          <FileDropzone
            multiple
            compact
            imageOnly
            privacyNotice
            accept={mediaAccept}
            files={mediaFiles.map((item) => item.file)}
            onFilesChange={(nextFiles) => replaceCategory("media", nextFiles)}
            onRedactImage={(index) => {
              setIsExpanded(true);
              setRedactMediaIndex(index);
            }}
            title="Kép vagy videó csatolása"
            description="JPG, PNG, GIF, MP4, MOV, WebM, legfeljebb 15 MB"
          />
        ) : null}

        <ImageRedactionEditor
          open={redactDraft != null}
          file={redactDraft?.file ?? null}
          onOpenChange={(open) => {
            if (!open) {
              setRedactMediaIndex(null);
            }
          }}
          onApply={(nextFile) => {
            if (!redactDraft) {
              return;
            }
            setFiles((current) =>
              current.map((item) =>
                item.id === redactDraft.id
                  ? { ...item, file: nextFile }
                  : item,
              ),
            );
            setRedactMediaIndex(null);
          }}
        />

        {intent !== "poll" && linkPreview.loading ? (
          <div className="overflow-hidden rounded-xl border bg-muted/20">
            <div className="h-24 animate-pulse bg-muted" />
            <div className="space-y-2 px-3 py-2.5">
              <div className="h-3 w-24 animate-pulse rounded bg-muted" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ) : null}

        {intent !== "poll" && linkPreview.preview && !linkPreview.loading ? (
          <LinkPreviewCard
            preview={linkPreview.preview}
            onRemove={linkPreview.dismiss}
          />
        ) : null}

        {formError ? (
          <p className="text-sm text-destructive">{formError}</p>
        ) : null}

        <div
          className={cn(
            "relative z-10 -mx-3 flex flex-col gap-3 border-t border-slate-100 bg-card px-3 pt-4 pb-1 md:-mx-5 md:px-5 xl:flex-row xl:items-center",
            hero && !isExpanded && "max-md:hidden",
          )}
        >
          <div
            className={cn(
              "min-w-0 flex-1",
              isExpanded
                ? "flex items-center justify-between gap-2 md:grid md:grid-cols-2 lg:grid-cols-4"
                : "flex items-center gap-2",
            )}
          >
            <ComposerAction
              compact={!isExpanded}
              active={picker === "document" || documentFiles.length > 0}
              icon={Paperclip}
              iconClassName="text-blue-600"
              title="Dokumentum"
              subtitle="Fájl csatolása"
              onClick={() => {
                setIsExpanded(true);
                setIntent("post");
                setPicker((current) =>
                  current === "document" ? null : "document",
                );
              }}
            />
            <ComposerAction
              compact={!isExpanded}
              active={picker === "media" || mediaFiles.length > 0}
              icon={ImageIcon}
              iconClassName="text-emerald-600"
              title="Kép vagy videó"
              subtitle="Média megosztása"
              onClick={() => {
                setIsExpanded(true);
                setIntent("post");
                setPicker((current) => (current === "media" ? null : "media"));
              }}
            />
            <ComposerAction
              compact={!isExpanded}
              active={intent === "question"}
              icon={MessageCircleQuestionMark}
              iconClassName="text-violet-600"
              title="Szakmai kérdés"
              subtitle="Kérdezze a közösséget"
              onClick={() => {
                setIsExpanded(true);
                setPicker(null);
                setIntent((current) =>
                  current === "question" ? "post" : "question",
                );
              }}
            />
            <ComposerAction
              compact={!isExpanded}
              active={intent === "poll"}
              icon={ChartColumn}
              iconClassName="text-sky-700"
              title="Szavazás"
              subtitle="Vélemények gyűjtése"
              onClick={() => {
                setIsExpanded(true);
                setPicker(null);
                setFiles([]);
                setFormError(null);
                setIntent((current) => (current === "poll" ? "post" : "poll"));
              }}
            />
          </div>
          <Button
            type="submit"
            disabled={createPost.isPending}
            className="h-11 w-full shrink-0 rounded-xl px-6 text-sm shadow-sm xl:ml-auto xl:w-auto xl:min-w-32"
          >
            {createPost.isPending ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : null}
            Közzététel
          </Button>
        </div>
      </form>
    </section>
  );
}

function PollEditor({
  options,
  allowOther,
  allowMultiple,
  onOptionsChange,
  onAllowOtherChange,
  onAllowMultipleChange,
}: {
  options: DraftPollOption[];
  allowOther: boolean;
  allowMultiple: boolean;
  onOptionsChange: (options: DraftPollOption[]) => void;
  onAllowOtherChange: (value: boolean) => void;
  onAllowMultipleChange: (value: boolean) => void;
}) {
  return (
    <div className="rounded-xl border border-sky-100 bg-sky-50/45 p-4">
      <div className="mb-3">
        <p className="text-sm font-semibold text-slate-900">
          Válaszlehetőségek
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Legalább kettő, legfeljebb tíz válasz adható meg.
        </p>
      </div>

      <div className="space-y-2">
        {options.map((option, index) => (
          <div key={option.id} className="flex items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-semibold text-sky-700 ring-1 ring-sky-100">
              {index + 1}
            </span>
            <input
              type="text"
              value={option.label}
              maxLength={120}
              placeholder={`${index + 1}. válaszlehetőség`}
              className="h-9 min-w-0 flex-1 rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              onChange={(event) =>
                onOptionsChange(
                  options.map((item) =>
                    item.id === option.id
                      ? { ...item, label: event.target.value }
                      : item,
                  ),
                )
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground hover:text-destructive"
              disabled={options.length <= 2}
              aria-label={`${index + 1}. válasz törlése`}
              onClick={() =>
                onOptionsChange(
                  options.filter((item) => item.id !== option.id),
                )
              }
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3 rounded-full bg-white text-xs"
        disabled={options.length >= 10}
        onClick={() =>
          onOptionsChange([
            ...options,
            { id: createId(), label: "" },
          ])
        }
      >
        <Plus className="size-3.5" />
        Válasz hozzáadása
      </Button>

      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-sky-100 pt-3">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={allowOther}
            className="size-4 rounded border-slate-300 accent-primary"
            onChange={(event) => onAllowOtherChange(event.target.checked)}
          />
          „Egyéb” válasz engedélyezése
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={allowMultiple}
            className="size-4 rounded border-slate-300 accent-primary"
            onChange={(event) => onAllowMultipleChange(event.target.checked)}
          />
          Több válasz is megjelölhető
        </label>
      </div>
    </div>
  );
}

function ComposerAction({
  active = false,
  compact = false,
  icon: Icon,
  iconClassName,
  title,
  subtitle,
  onClick,
}: {
  active?: boolean;
  compact?: boolean;
  icon: typeof Paperclip;
  iconClassName: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={compact ? `${title} — ${subtitle}` : undefined}
      aria-label={title}
      className={cn(
        "flex items-center rounded-xl border text-left transition-colors",
        compact
          ? "size-11 shrink-0 justify-center"
          : "min-h-12 min-w-0 gap-2 px-2.5 py-2 max-md:size-11 max-md:min-h-0 max-md:shrink-0 max-md:justify-center max-md:rounded-full max-md:p-0",
        active
          ? "border-primary/30 bg-primary/5"
          : "border-black/[0.06] bg-card hover:bg-muted/40",
      )}
    >
      <Icon
        className={cn(
          "shrink-0",
          compact ? "size-5" : "size-3.5 max-md:size-5",
          iconClassName,
        )}
      />
      {compact ? null : (
        <span className="min-w-0 max-md:hidden">
          <span className="block truncate text-[12px] font-semibold leading-4 text-slate-800">
            {title}
          </span>
          <span className="block truncate text-[9px] leading-3 text-muted-foreground">
            {subtitle}
          </span>
        </span>
      )}
    </button>
  );
}

function ComposerIllustration({ home }: { home: boolean }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 right-0 hidden h-[190px] w-[330px] overflow-hidden sm:block"
    >
      <Image
        src={
          home
            ? "/brand/post-composer-home.png"
            : "/brand/post-composer-chat.png"
        }
        alt=""
        width={home ? 1024 : 644}
        height={home ? 824 : 465}
        className={cn(
          "absolute h-auto max-w-none",
          home
            ? "-top-10 -right-3 w-[280px] opacity-95"
            : "-top-16 -right-12 w-[310px] opacity-90",
        )}
        priority
      />
    </div>
  );
}
